import { ErrorBanner } from '@core/components';
import type { CustomLabel } from '@core/graphql/types';
import { LabelType } from '@core/graphql/types';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { useCreateCustomLabel, useUpdateCustomLabel } from '@moderation/hooks';
import { TextInput, LoadingSpinner, Switch, Dialog, TextArea, Text } from '@soteria/design-system-ve';
import { useEffect, useState } from 'react';

import * as styles from './ModerationLabelDialog.css';

export interface ModerationLabelDialogProps {
  isOpen: boolean;
  onClose: () => void;
  communityId: string;
  label?: CustomLabel;
}

interface FormData {
  name: string;
  description: string;
  labelType: LabelType;
  active: boolean;
}

function ModerationLabelDialogComponent({ isOpen, onClose, communityId, label }: ModerationLabelDialogProps) {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    labelType: LabelType.Moderation,
    active: true,
  });

  useEffect(() => {
    // Reset form data when dialog opens or label changes
    if (isOpen) {
      setFormData({
        name: label?.name || '',
        description: label?.description || '',
        labelType: label?.labelType || LabelType.Moderation,
        active: label?.active ?? true,
      });
    }
  }, [isOpen, label]);

  const { createCustomLabel, loading: isCreating, errors: createErrors, resetErrors: resetCreateErrors } = useCreateCustomLabel(communityId);
  const { updateCustomLabel, loading: isUpdating, errors: updateErrors, resetErrors: resetUpdateErrors } = useUpdateCustomLabel(communityId);

  const isLoading = isCreating || isUpdating;
  const errors = createErrors || updateErrors;

  const isFormValid = () => {
    // Check required fields
    const requiredFieldsValid = formData.name.trim() !== '' && formData.description.trim() !== '';

    // If editing, also check if anything changed
    if (label) {
      const hasChanges =
        formData.name !== label.name ||
        formData.description !== label.description ||
        formData.labelType !== label.labelType ||
        formData.active !== label.active;

      return requiredFieldsValid && hasChanges;
    }

    return requiredFieldsValid;
  };

  const handleInputChange = (field: keyof FormData) => (value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    resetCreateErrors();
    resetUpdateErrors();

    if (label) {
      // Update existing label
      const success = await updateCustomLabel(label.id, {
        name: formData.name,
        description: formData.description,
        labelType: formData.labelType,
        active: formData.active,
      });

      if (success) {
        onClose();
      }
    } else {
      // Create new label
      const success = await createCustomLabel({
        name: formData.name,
        description: formData.description,
        labelType: formData.labelType,
        active: formData.active,
      });

      if (success) {
        onClose();
      }
    }
  };

  return (
    <Dialog
      cancelAction={{ label: 'Close' }}
      description={!label ? 'Create a new moderation label for this community' : 'Edit the moderation label'}
      isOpen={isOpen}
      primaryAction={{
        label: isLoading ? <LoadingSpinner /> : label ? 'Save Changes' : 'Add Label',
        onClick: handleSubmit,
        disabled: isLoading || !isFormValid(),
      }}
      size="md"
      title={label ? `Edit ${label.name}` : 'Add Moderation Label'}
      onClose={() => {
        resetCreateErrors();
        resetUpdateErrors();
        onClose();
      }}
    >
      <div className={styles.form}>
        {errors && errors.length > 0 && <ErrorBanner errors={errors} title="There was an error saving the label:" />}

        {/* Active Toggle */}
        <div className={styles.toggleContainer}>
          <span className={styles.toggleLabel}>Active</span>
          <Switch
            checked={formData.active}
            id="active"
            label="Toggle label active status"
            onChange={(checked: boolean) => {
              handleInputChange('active')(checked);
            }}
          />
        </div>

        {/* Label Name Input */}
        <TextInput required id="name" label="Label Name" placeholder="Enter label name" value={formData.name} onChange={handleInputChange('name')} />

        {/* Description TextArea */}
        <div>
          <Text>Description*</Text>
          <TextArea
            required
            id="description"
            placeholder="Describe the purpose of this label"
            rows={3}
            value={formData.description}
            onChange={event => handleInputChange('description')(event.target.value)}
          />
        </div>

        {/* Label Type is hardcoded to "moderation" - no input needed */}
      </div>
    </Dialog>
  );
}

export const ModerationLabelDialog = withAppInsightsTracking(ModerationLabelDialogComponent, 'ModerationLabelDialog');
